import React, { useState, useEffect } from 'react';
import { Container, Card, Button, Alert, Row, Col, Table, Nav, Tabs, Tab, Badge, Modal, Form } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  updateDoc,
  getDoc,
  addDoc,
  setDoc,
  orderBy
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { FaTools, FaDatabase, FaWrench, FaUsers, FaBook, FaComments, FaCalendarAlt, FaChartBar, FaCog, FaUserTie, FaFileDownload } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { calculateSeminarScore } from '../utils/seminarUtils';
import { generateLogbookPDFAdmin } from '../utils/pdfGenerator';

// BimbinganDetailView component for displaying bimbingan details in the modal
const BimbinganDetailView = ({ tugasAkhirId }) => {
  const [bimbinganList, setBimbinganList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchBimbinganData = async () => {
      if (!tugasAkhirId) return;

      try {
        setLoading(true);
        setError('');

        // Fetch bimbingan data
        const bimbinganQuery = query(
          collection(db, "bimbingan"),
          where("tugasAkhirId", "==", tugasAkhirId),
          orderBy("tanggal", "desc")
        );

        const bimbinganSnapshot = await getDocs(bimbinganQuery);

        // Process bimbingan data
        const bimbinganData = [];
        for (const bimbinganDoc of bimbinganSnapshot.docs) {
          const bimbinganItem = { id: bimbinganDoc.id, ...bimbinganDoc.data() };

          // Skip deleted bimbingan
          if (bimbinganItem.deleted === true) continue;

          // Get pembimbing name if needed
          if (bimbinganItem.pembimbingId && !bimbinganItem.pembimbingNama) {
            const pembimbingDoc = await getDoc(doc(db, "users", bimbinganItem.pembimbingId));
            if (pembimbingDoc.exists()) {
              bimbinganItem.pembimbingNama = pembimbingDoc.data().nama;
            }
          }

          // Get mahasiswa name if needed
          if (bimbinganItem.mahasiswaId && !bimbinganItem.mahasiswaNama) {
            const mahasiswaDoc = await getDoc(doc(db, "users", bimbinganItem.mahasiswaId));
            if (mahasiswaDoc.exists()) {
              bimbinganItem.mahasiswaNama = mahasiswaDoc.data().nama;
            }
          }

          bimbinganData.push(bimbinganItem);
        }

        setBimbinganList(bimbinganData);
      } catch (error) {
        console.error("Error fetching bimbingan:", error);
        setError("Terjadi kesalahan saat mengambil data bimbingan");
      } finally {
        setLoading(false);
      }
    };

    fetchBimbinganData();
  }, [tugasAkhirId]);

  if (loading) {
    return <Alert variant="info">Memuat data bimbingan...</Alert>;
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  if (bimbinganList.length === 0) {
    return <Alert variant="warning">Belum ada data bimbingan</Alert>;
  }

  return (
    <div>
      <Table responsive striped bordered hover>
        <thead>
          <tr>
            <th>Tanggal</th>
            <th>Sesi</th>
            <th>Pembimbing</th>
            <th>Judul</th>
            <th>Jenis</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {bimbinganList.map((bimbingan, index) => (
            <tr key={index}>
              <td>{new Date(bimbingan.tanggal).toLocaleDateString()}</td>
              <td>{bimbingan.sesi || '-'}</td>
              <td>{bimbingan.pembimbingNama || '-'}</td>
              <td>
                {bimbingan.judul}
                {bimbingan.catatan && (
                  <div className="mt-1">
                    <small className="text-muted">{bimbingan.catatan}</small>
                  </div>
                )}
              </td>
              <td>
                <Badge bg="info">
                  {bimbingan.jenisBimbingan === 'hasil' ? 'Hasil' :
                    bimbingan.jenisBimbingan === 'finalisasi' ? 'Finalisasi' : 'Proposal'}
                </Badge>
              </td>
              <td>
                <Badge bg={bimbingan.status === 'reviewed' ? 'success' : 'warning'}>
                  {bimbingan.status === 'reviewed' ? 'Direview' : 'Pending'}
                </Badge>
                {bimbingan.status === 'reviewed' && bimbingan.reviewCatatan && (
                  <div className="mt-1">
                    <small>{bimbingan.reviewCatatan}</small>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

// Helper function to calculate final score for seminar modal
const calculateFinalScore = (seminar) => {
  if (!seminar || !seminar.nilai || typeof seminar.nilai !== 'object') return 0;

  // Use the shared score calculation utility
  const result = calculateSeminarScore(seminar);

  // Return the calculated final score
  return result.finalScore;
};

const Admin = () => {
  const { currentUser, userRole } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [diagnosticResults, setDiagnosticResults] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [mahasiswaData, setMahasiswaData] = useState([]);
  const [dosenData, setDosenData] = useState([]);
  const [bimbinganData, setBimbinganData] = useState([]);
  const [seminarData, setSeminarData] = useState([]);
  const [mahasiswaCount, setMahasiswaCount] = useState(0);
  const [dosenCount, setDosenCount] = useState(0);
  const [selectedDetailView, setSelectedDetailView] = useState(null);
  const [selectedTugasAkhir, setSelectedTugasAkhir] = useState(null);
  const [selectedSeminar, setSelectedSeminar] = useState(null);
  const [koordinatorTA, setKoordinatorTA] = useState(null);
  const [selectedKoordinatorId, setSelectedKoordinatorId] = useState('');
  const [showLogbookModal, setShowLogbookModal] = useState(false);
  const [selectedLogbookMahasiswa, setSelectedLogbookMahasiswa] = useState(null);
  const [logbookMode, setLogbookMode] = useState('both');
  const [loadingLogbook, setLoadingLogbook] = useState(false);
  const navigate = useNavigate();

  const [loadedTabs, setLoadedTabs] = useState(new Set());

  // Periksa apakah pengguna punya hak akses admin
  useEffect(() => {
    if (userRole !== 'pj' && userRole !== 'admin') {
      navigate('/dashboard');
    } else {
      // Load data hanya untuk tab yang aktif (dashboard)
      if (activeTab === 'dashboard') {
        loadDashboardData();
      }
      fetchKoordinatorTA();
    }
  }, [userRole, navigate]);

  // Load data ketika tab berubah
  useEffect(() => {
    if (!loadedTabs.has(activeTab)) {
      if (activeTab === 'dashboard') {
        loadDashboardData();
      } else if (activeTab === 'mahasiswa') {
        loadMahasiswaData();
      } else if (activeTab === 'dosen') {
        loadDosenData();
      }
    }
  }, [activeTab]);

  // Load dashboard data (summary + recent activities)
  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch counts untuk dashboard cards (quick summary)
      const mahasiswaQuery = query(
        collection(db, "users"),
        where("role", "==", "mahasiswa"),
        where("verified", "==", true)
      );
      const mahasiswaSnapshot = await getDocs(mahasiswaQuery);

      const dosenQuery = query(
        collection(db, "users"),
        where("role", "==", "dosen"),
        where("verified", "==", true)
      );
      const dosenSnapshot = await getDocs(dosenQuery);

      // Fetch recent bimbingan (top 20)
      const recentBimbinganQuery = query(
        collection(db, "bimbingan"),
        orderBy("createdAt", "desc")
      );
      const recentBimbinganSnapshot = await getDocs(recentBimbinganQuery);

      const bimbinganList = [];
      for (const bimbinganDoc of recentBimbinganSnapshot.docs) {
        if (bimbinganList.length >= 20) break;

        const bimbinganItem = { id: bimbinganDoc.id, ...bimbinganDoc.data() };
        if (bimbinganItem.deleted === true) continue;

        if (bimbinganItem.mahasiswaId) {
          const mahasiswaDoc = await getDoc(doc(db, "users", bimbinganItem.mahasiswaId));
          if (mahasiswaDoc.exists()) {
            bimbinganItem.mahasiswaNama = mahasiswaDoc.data().nama;
          }
        }

        if (bimbinganItem.pembimbingId) {
          const pembimbingDoc = await getDoc(doc(db, "users", bimbinganItem.pembimbingId));
          if (pembimbingDoc.exists()) {
            bimbinganItem.pembimbingNama = pembimbingDoc.data().nama;
          }
        }

        bimbinganList.push(bimbinganItem);
      }

      setBimbinganData(bimbinganList);

      // Fetch recent seminar (top 20)
      const recentSeminarQuery = query(
        collection(db, "seminar"),
        orderBy("tanggal", "desc")
      );
      const recentSeminarSnapshot = await getDocs(recentSeminarQuery);

      const seminarList = [];
      for (const seminarDoc of recentSeminarSnapshot.docs) {
        if (seminarList.length >= 20) break;

        const seminarItem = { id: seminarDoc.id, ...seminarDoc.data() };

        if (!seminarItem.mahasiswaNama && seminarItem.tugasAkhirId) {
          const taDoc = await getDoc(doc(db, "tugasAkhir", seminarItem.tugasAkhirId));
          if (taDoc.exists()) {
            const taData = taDoc.data();
            if (taData.mahasiswaId) {
              const mahasiswaDoc = await getDoc(doc(db, "users", taData.mahasiswaId));
              if (mahasiswaDoc.exists()) {
                seminarItem.mahasiswaNama = mahasiswaDoc.data().nama;
              }
            }
          }
        }

        if (seminarItem.status === 'completed' && seminarItem.nilai) {
          seminarItem.reviewersCount = Object.keys(seminarItem.nilai).length;
          if (seminarItem.reviewersCount > 0) {
            let tugasAkhirData = null;
            if (seminarItem.tugasAkhirId) {
              const taDoc = await getDoc(doc(db, "tugasAkhir", seminarItem.tugasAkhirId));
              if (taDoc.exists()) {
                tugasAkhirData = taDoc.data();
              }
            }
            const scoreResult = calculateSeminarScore(seminarItem, tugasAkhirData);
            seminarItem.evaluatorScores = scoreResult.evaluatorScores;
            seminarItem.finalScore = scoreResult.finalScore;
            seminarItem.passed = scoreResult.isPassed;
            seminarItem.isReschedule = seminarItem.isReschedule || false;
          }
        }

        seminarList.push(seminarItem);
      }

      setSeminarData(seminarList);

      // Simpan count untuk dashboard cards
      // Dashboard hanya butuh count, tidak perlu data lengkap
      const mahasiswaTotal = mahasiswaSnapshot.size;
      const dosenTotal = dosenSnapshot.size;

      setMahasiswaCount(mahasiswaTotal);
      setDosenCount(dosenTotal);

      setLoadedTabs(prev => new Set([...prev, 'dashboard']));

    } catch (error) {
      console.error("Error loading dashboard data:", error);
      setError("Terjadi kesalahan saat memuat data dashboard: " + error.message);
      setMahasiswaData([]);
      setDosenData([]);
      setBimbinganData([]);
      setSeminarData([]);
    } finally {
      setLoading(false);
    }
  };

  // Load mahasiswa monitoring data
  const loadMahasiswaData = async () => {
    try {
      setLoading(true);
      setError('');

      const mahasiswaQuery = query(
        collection(db, "users"),
        where("role", "==", "mahasiswa"),
        where("verified", "==", true)
      );
      const mahasiswaSnapshot = await getDocs(mahasiswaQuery);
      const mahasiswaList = [];

      for (const mahasiswaDoc of mahasiswaSnapshot.docs) {
        const mahasiswaUser = { id: mahasiswaDoc.id, ...mahasiswaDoc.data() };

        const taQuery = query(
          collection(db, "tugasAkhir"),
          where("mahasiswaId", "==", mahasiswaDoc.id)
        );
        const taSnapshot = await getDocs(taQuery);

        if (!taSnapshot.empty) {
          const taData = taSnapshot.docs[0].data();
          taData.id = taSnapshot.docs[0].id;
          mahasiswaUser.tugasAkhir = taData;

          const bimbinganQuery = query(
            collection(db, "bimbingan"),
            where("tugasAkhirId", "==", taData.id)
          );
          const bimbinganSnapshot = await getDocs(bimbinganQuery);

          const validBimbingan = bimbinganSnapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(b => b.deleted !== true);

          mahasiswaUser.bimbinganCounts = {
            total: validBimbingan.length,
            proposal: validBimbingan.filter(b => !b.jenisBimbingan || b.jenisBimbingan === 'proposal').length,
            hasil: validBimbingan.filter(b => b.jenisBimbingan === 'hasil').length,
            finalisasi: validBimbingan.filter(b => b.jenisBimbingan === 'finalisasi').length
          };

          const seminarQuery = query(
            collection(db, "seminar"),
            where("tugasAkhirId", "==", taData.id)
          );
          const seminarSnapshot = await getDocs(seminarQuery);

          const seminarList = seminarSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          mahasiswaUser.seminars = seminarList;

          mahasiswaUser.allSeminars = {
            proposal: seminarList.filter(s => s.jenis === 'proposal'),
            hasil: seminarList.filter(s => s.jenis === 'hasil'),
            kompre: seminarList.filter(s => s.jenis === 'kompre')
          };

          mahasiswaUser.seminarStatus = {
            proposal: seminarList.find(s => s.jenis === 'proposal'),
            hasil: seminarList.find(s => s.jenis === 'hasil'),
            kompre: seminarList.find(s => s.jenis === 'kompre')
          };
        }

        mahasiswaList.push(mahasiswaUser);
      }

      setMahasiswaData(mahasiswaList);
      setLoadedTabs(prev => new Set([...prev, 'mahasiswa']));

    } catch (error) {
      console.error("Error loading mahasiswa data:", error);
      setError("Terjadi kesalahan saat memuat data mahasiswa: " + error.message);
      setMahasiswaData([]);
    } finally {
      setLoading(false);
    }
  };

  // Load dosen monitoring data
  const loadDosenData = async () => {
    try {
      setLoading(true);
      setError('');

      const dosenQuery = query(
        collection(db, "users"),
        where("role", "==", "dosen"),
        where("verified", "==", true)
      );
      const dosenSnapshot = await getDocs(dosenQuery);
      const dosenList = [];

      for (const dosenDoc of dosenSnapshot.docs) {
        const dosenUser = { id: dosenDoc.id, ...dosenDoc.data() };

        const bimbinganQuery = query(
          collection(db, "bimbingan"),
          where("pembimbingId", "==", dosenDoc.id)
        );
        const bimbinganSnapshot = await getDocs(bimbinganQuery);

        const validBimbingan = bimbinganSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(b => b.deleted !== true);

        dosenUser.bimbinganCounts = {
          total: validBimbingan.length,
          reviewed: validBimbingan.filter(b => b.status === 'reviewed').length,
          pending: validBimbingan.filter(b => b.status === 'pending').length
        };

        const uniqueMahasiswaIds = new Set();
        validBimbingan.forEach(b => {
          if (b.mahasiswaId) uniqueMahasiswaIds.add(b.mahasiswaId);
        });
        dosenUser.mahasiswaCount = uniqueMahasiswaIds.size;

        dosenList.push(dosenUser);
      }

      setDosenData(dosenList);
      setLoadedTabs(prev => new Set([...prev, 'dosen']));

    } catch (error) {
      console.error("Error loading dosen data:", error);
      setError("Terjadi kesalahan saat memuat data dosen: " + error.message);
      setDosenData([]);
    } finally {
      setLoading(false);
    }
  };

  // Wrapper untuk backward compatibility
  const loadAdminData = async () => {
    if (activeTab === 'dashboard') {
      await loadDashboardData();
    } else if (activeTab === 'mahasiswa') {
      await loadMahasiswaData();
    } else if (activeTab === 'dosen') {
      await loadDosenData();
    }
  };

  // Fetch koordinator tugas akhir
  const fetchKoordinatorTA = async () => {
    try {
      const settingsDoc = await getDoc(doc(db, "settings", "koordinatorTA"));
      if (settingsDoc.exists()) {
        const settingsData = settingsDoc.data();
        if (settingsData.koordinatorId) {
          const koordinatorDoc = await getDoc(doc(db, "users", settingsData.koordinatorId));
          if (koordinatorDoc.exists()) {
            const koordinatorData = koordinatorDoc.data();
            setKoordinatorTA({
              id: koordinatorDoc.id,
              ...koordinatorData
            });
            setSelectedKoordinatorId(koordinatorDoc.id);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching koordinator TA:", error);
    }
  };

  // Set koordinator tugas akhir
  const handleSetKoordinatorTA = async () => {
    try {
      if (!selectedKoordinatorId) {
        setError("Silakan pilih dosen sebagai koordinator");
        return;
      }

      setLoading(true);
      setError('');

      // Simpan ke settings collection
      await setDoc(doc(db, "settings", "koordinatorTA"), {
        koordinatorId: selectedKoordinatorId,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser.uid
      }, { merge: true });

      // Update state
      const koordinatorDoc = await getDoc(doc(db, "users", selectedKoordinatorId));
      if (koordinatorDoc.exists()) {
        const koordinatorData = koordinatorDoc.data();
        setKoordinatorTA({
          id: koordinatorDoc.id,
          ...koordinatorData
        });
      }

      setSuccess("Koordinator Tugas Akhir berhasil diatur");
    } catch (error) {
      console.error("Error setting koordinator TA:", error);
      setError("Terjadi kesalahan saat mengatur koordinator: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle generate logbook PDF from admin
  const handleGenerateLogbook = async () => {
    if (!selectedLogbookMahasiswa) return;

    try {
      setLoadingLogbook(true);
      setError('');

      const mahasiswa = selectedLogbookMahasiswa;
      const ta = mahasiswa.tugasAkhir;

      if (!ta || !ta.id) {
        setError('Data tugas akhir tidak ditemukan');
        return;
      }

      // 1. Get pembimbing data with signatures
      const pembimbingList = [];
      if (ta.pembimbingId && Array.isArray(ta.pembimbingId)) {
        for (const pembId of ta.pembimbingId) {
          try {
            const pembDoc = await getDoc(doc(db, "users", pembId));
            if (pembDoc.exists()) {
              const pembData = pembDoc.data();
              pembimbingList.push({
                id: pembId,
                nama: pembData.nama || 'Dosen',
                nip: pembData.nip || '-',
                signatureURL: pembData.signatureURL || null
              });
            }
          } catch (err) {
            console.error("Error fetching pembimbing:", err);
          }
        }
      }

      // Check if selected mode is valid
      if (logbookMode === 'p2' && pembimbingList.length < 2) {
        setError('Pembimbing 2 tidak tersedia');
        setLoadingLogbook(false);
        return;
      }

      // 2. Fetch all bimbingan for this TA
      const bimbinganQuery = query(
        collection(db, "bimbingan"),
        where("tugasAkhirId", "==", ta.id)
      );
      const bimbinganSnapshot = await getDocs(bimbinganQuery);

      const bimbinganList = [];
      bimbinganSnapshot.forEach(docSnap => {
        const data = docSnap.data();
        if (data.deleted !== true) {
          bimbinganList.push({
            id: docSnap.id,
            ...data
          });
        }
      });

      console.log(`Found ${bimbinganList.length} bimbingan for logbook`);

      // 3. Prepare data
      const tugasAkhirData = {
        id: ta.id,
        judul: ta.judul,
        prodi: mahasiswa.prodi
      };

      const mahasiswaData = {
        nama: mahasiswa.nama || 'Mahasiswa',
        nim: mahasiswa.nim || '-',
        prodi: mahasiswa.prodi || 'REKAYASA KOSMETIK'
      };

      // 4. Generate PDF
      const pdfBlob = await generateLogbookPDFAdmin(tugasAkhirData, mahasiswaData, bimbinganList, pembimbingList, logbookMode);

      // 5. Download PDF
      const modeLabel = logbookMode === 'p1' ? '_P1' : logbookMode === 'p2' ? '_P2' : '_Gabungan';
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Logbook_Bimbingan_${mahasiswaData.nim}${modeLabel}_${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSuccess('Logbook berhasil di-generate dan didownload.');
      setShowLogbookModal(false);
    } catch (error) {
      console.error("Error generating logbook:", error);
      setError("Terjadi kesalahan saat generate logbook: " + error.message);
    } finally {
      setLoadingLogbook(false);
    }
  };

  // Fungsi untuk menjalankan diagnostik database
  const runDiagnostics = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const results = {
        users: [],
        tugasAkhir: [],
        bimbingan: [],
        seminar: []
      };

      // Ambil sampel data pengguna
      const usersSnapshot = await getDocs(collection(db, "users"));
      usersSnapshot.forEach(doc => {
        if (results.users.length < 5) {  // Hanya ambil 5 sampel
          results.users.push({ id: doc.id, ...doc.data() });
        }
      });

      // Ambil sampel data tugas akhir
      const taSnapshot = await getDocs(collection(db, "tugasAkhir"));
      taSnapshot.forEach(doc => {
        if (results.tugasAkhir.length < 5) {
          results.tugasAkhir.push({ id: doc.id, ...doc.data() });
        }
      });

      // Ambil sampel data bimbingan
      const bimbinganSnapshot = await getDocs(collection(db, "bimbingan"));
      bimbinganSnapshot.forEach(doc => {
        if (results.bimbingan.length < 5) {
          results.bimbingan.push({ id: doc.id, ...doc.data() });
        }
      });

      // Ambil sampel data seminar
      const seminarSnapshot = await getDocs(collection(db, "seminar"));
      seminarSnapshot.forEach(doc => {
        if (results.seminar.length < 5) {
          results.seminar.push({ id: doc.id, ...doc.data() });
        }
      });

      // Hitung total data
      results.counts = {
        users: usersSnapshot.size,
        tugasAkhir: taSnapshot.size,
        bimbingan: bimbinganSnapshot.size,
        seminar: seminarSnapshot.size
      };

      // Cek masalah umum
      results.issues = {
        bimbinganWithoutPembimbing: 0,
        taWithoutPembimbing: 0,
        pendingBimbingan: 0
      };

      // Cek bimbingan tanpa pembimbingId
      bimbinganSnapshot.forEach(doc => {
        const data = doc.data();
        if (!data.pembimbingId) {
          results.issues.bimbinganWithoutPembimbing++;
        }
        if (data.status === 'pending') {
          results.issues.pendingBimbingan++;
        }
      });

      // Cek tugas akhir tanpa pembimbingId
      taSnapshot.forEach(doc => {
        const data = doc.data();
        if (data.status === 'approved' &&
          (!data.pembimbingId ||
            (Array.isArray(data.pembimbingId) && data.pembimbingId.length === 0))) {
          results.issues.taWithoutPembimbing++;
        }
      });

      // Tampilkan hasil
      setDiagnosticResults(results);
      console.log("Hasil diagnostik:", results);
      setSuccess("Diagnostik database berhasil dijalankan");

    } catch (error) {
      console.error("Error running diagnostics:", error);
      setError("Terjadi kesalahan saat menjalankan diagnostik: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Fungsi untuk memperbaiki masalah database
  const fixDatabaseIssues = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      let fixedBimbingan = 0;
      let fixedTA = 0;

      // 1. Perbaiki bimbingan tanpa pembimbingId
      const bimbinganSnapshot = await getDocs(collection(db, "bimbingan"));

      for (const bimbinganDoc of bimbinganSnapshot.docs) {
        const bimbinganData = bimbinganDoc.data();

        // Jika tidak ada pembimbingId
        if (!bimbinganData.pembimbingId && bimbinganData.tugasAkhirId) {
          try {
            // Ambil data tugas akhir
            const taDoc = await getDoc(doc(db, "tugasAkhir", bimbinganData.tugasAkhirId));

            if (taDoc.exists()) {
              const taData = taDoc.data();

              // Ambil pembimbingId dari tugas akhir
              if (taData.pembimbingId) {
                let pembimbingId;

                if (Array.isArray(taData.pembimbingId)) {
                  if (taData.pembimbingId.length > 0) {
                    pembimbingId = taData.pembimbingId[0]; // Ambil pembimbing pertama
                  }
                } else {
                  pembimbingId = taData.pembimbingId;
                }

                if (pembimbingId) {
                  // Update data bimbingan
                  await updateDoc(bimbinganDoc.ref, { pembimbingId });
                  fixedBimbingan++;
                }
              }
            }
          } catch (error) {
            console.error("Error fixing bimbingan:", error);
          }
        }
      }

      // 2. Perbaiki tugas akhir yang disetujui tapi tidak ada pembimbingId
      const taApprovedQuery = query(
        collection(db, "tugasAkhir"),
        where("status", "==", "approved")
      );
      const taApprovedSnapshot = await getDocs(taApprovedQuery);

      for (const taDoc of taApprovedSnapshot.docs) {
        const taData = taDoc.data();

        // Jika tidak ada pembimbingId atau kosong
        if (!taData.pembimbingId ||
          (Array.isArray(taData.pembimbingId) && taData.pembimbingId.length === 0)) {

          // Cari dosen yang bisa jadi pembimbing
          const dosenQuery = query(
            collection(db, "users"),
            where("role", "==", "dosen"),
            where("verified", "==", true)
          );
          const dosenSnapshot = await getDocs(dosenQuery);

          if (!dosenSnapshot.empty) {
            // Ambil dosen pertama sebagai pembimbing
            const dosenDoc = dosenSnapshot.docs[0];
            const pembimbingId = [dosenDoc.id]; // Array dengan 1 dosen

            // Update tugas akhir
            await updateDoc(taDoc.ref, { pembimbingId });
            fixedTA++;
          }
        }
      }

      setSuccess(`Perbaikan database selesai. Diperbaiki: ${fixedBimbingan} bimbingan, ${fixedTA} tugas akhir.`);

      // Jalankan diagnostik lagi untuk memperbarui hasil
      runDiagnostics();

      // Refresh data sesuai tab aktif
      if (activeTab === 'dashboard') {
        loadDashboardData();
      } else if (activeTab === 'mahasiswa') {
        loadMahasiswaData();
      } else if (activeTab === 'dosen') {
        loadDosenData();
      }

    } catch (error) {
      console.error("Error fixing database:", error);
      setError("Terjadi kesalahan saat memperbaiki database: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Function to generate a summary dashboard
  const renderDashboard = () => {
    // Show loading state
    if (loading && !loadedTabs.has('dashboard')) {
      return (
        <Card>
          <Card.Body>
            <Alert variant="info">Memuat data dashboard...</Alert>
          </Card.Body>
        </Card>
      );
    }

    // Use count from state (for dashboard) or data length (for other tabs)
    const displayedMahasiswaCount = loadedTabs.has('mahasiswa') ? mahasiswaData.length : mahasiswaCount;
    const displayedDosenCount = loadedTabs.has('dosen') ? dosenData.length : dosenCount;

    return (
      <div>
        <Row className="mb-4">
          <Col md={3}>
            <Card className="mb-3 text-center bg-primary text-white">
              <Card.Body>
                <FaUsers size={24} className="mb-2" />
                <h5>Mahasiswa</h5>
                <h2>{displayedMahasiswaCount}</h2>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="mb-3 text-center bg-success text-white">
              <Card.Body>
                <FaUsers size={24} className="mb-2" />
                <h5>Dosen</h5>
                <h2>{displayedDosenCount}</h2>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="mb-3 text-center bg-info text-white">
              <Card.Body>
                <FaComments size={24} className="mb-2" />
                <h5>Bimbingan</h5>
                <h2>
                  {bimbinganData.filter(b => b.status === 'pending').length} <small>pending</small>
                </h2>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="mb-3 text-center bg-warning text-white">
              <Card.Body>
                <FaCalendarAlt size={24} className="mb-2" />
                <h5>Seminar</h5>
                <h2>
                  {seminarData.filter(s => s.status === 'scheduled').length} <small>terjadwal</small>
                </h2>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Card className="mb-4">
              <Card.Header className="bg-primary text-white">
                <h5 className="mb-0"><FaCalendarAlt className="me-2" /> Seminar Terbaru</h5>
              </Card.Header>
              <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <Table responsive striped bordered hover>
                  <thead>
                    <tr>
                      <th>Tanggal</th>
                      <th>Mahasiswa</th>
                      <th>Jenis</th>
                      <th>Status</th>
                      <th>Nilai</th>
                    </tr>
                  </thead>
                  <tbody>
                    {seminarData.slice(0, 10).map((seminar, idx) => (
                      <tr key={idx}>
                        <td>{new Date(seminar.tanggal).toLocaleDateString()}</td>
                        <td>{seminar.mahasiswaNama}</td>
                        <td>
                          {seminar.jenis === 'proposal' ? 'Proposal' :
                            seminar.jenis === 'hasil' ? 'Hasil' : 'Kompre'}
                        </td>
                        <td>
                          <Badge bg={
                            seminar.status === 'completed' ? 'success' :
                              seminar.status === 'scheduled' ? 'info' : 'secondary'
                          }>
                            {seminar.status === 'completed' ? 'Selesai' :
                              seminar.status === 'scheduled' ? 'Terjadwal' : seminar.status}
                          </Badge>
                        </td>
                        <td>
                          {seminar.finalScore !== undefined ? (
                            <span>
                              {seminar.finalScore.toFixed(1)}{' '}
                              <Badge bg={seminar.passed ? 'success' : 'danger'} pill>
                                {seminar.passed ? 'Lulus' : 'Gagal'}
                              </Badge>
                            </span>
                          ) : (
                            seminar.reviewersCount > 0 ? (
                              `${seminar.reviewersCount}/4 penguji`
                            ) : '-'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Card className="mb-4">
              <Card.Header className="bg-success text-white">
                <h5 className="mb-0"><FaComments className="me-2" /> Bimbingan Terbaru</h5>
              </Card.Header>
              <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <Table responsive striped bordered hover>
                  <thead>
                    <tr>
                      <th>Tanggal</th>
                      <th>Mahasiswa</th>
                      <th>Pembimbing</th>
                      <th>Jenis</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bimbinganData.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center">
                          <Alert variant="info" className="mb-0">Belum ada data bimbingan</Alert>
                        </td>
                      </tr>
                    ) : (
                      bimbinganData.slice(0, 10).map((bimbingan, idx) => (
                        <tr key={idx}>
                          <td>{new Date(bimbingan.tanggal).toLocaleDateString()}</td>
                          <td>{bimbingan.mahasiswaNama || '-'}</td>
                          <td>{bimbingan.pembimbingNama || '-'}</td>
                          <td>
                            {bimbingan.jenisBimbingan === 'hasil' ? 'Hasil' :
                              bimbingan.jenisBimbingan === 'finalisasi' ? 'Finalisasi' : 'Proposal'}
                          </td>
                          <td>
                            <Badge bg={bimbingan.status === 'reviewed' ? 'success' : 'warning'}>
                              {bimbingan.status === 'reviewed' ? 'Direview' : 'Pending'}
                            </Badge>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  // Function to render the mahasiswa monitoring page
  const renderMahasiswaMonitoring = () => {
    return (
      <Card>
        <Card.Header className="bg-primary text-white">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0"><FaUsers className="me-2" /> Monitoring Mahasiswa</h5>
            <Button variant="light" size="sm" onClick={loadMahasiswaData}>
              Refresh Data
            </Button>
          </div>
        </Card.Header>
        <Card.Body style={{ maxHeight: '600px', overflowY: 'auto' }}>
          <Table responsive striped bordered hover>
            <thead>
              <tr>
                <th>Nama</th>
                <th>NIM</th>
                <th>Judul TA</th>
                <th>Bimbingan</th>
                <th>Seminar Proposal</th>
                <th>Seminar Hasil</th>
                <th>Sidang Kompre</th>
              </tr>
            </thead>
            <tbody>
              {mahasiswaData.map((mahasiswa, idx) => (
                <tr key={idx}>
                  <td>{mahasiswa.nama}</td>
                  <td>{mahasiswa.nim}</td>
                  <td>
                    {mahasiswa.tugasAkhir ? (
                      <>
                        {mahasiswa.tugasAkhir.judul}
                        <div>
                          <Badge bg={
                            mahasiswa.tugasAkhir.status === 'approved' ? 'success' :
                              mahasiswa.tugasAkhir.status === 'pending' ? 'warning' : 'danger'
                          }>
                            {mahasiswa.tugasAkhir.status === 'approved' ? 'Disetujui' :
                              mahasiswa.tugasAkhir.status === 'pending' ? 'Pending' : 'Ditolak'}
                          </Badge>
                        </div>
                      </>
                    ) : '-'}
                  </td>
                  <td>
                    {mahasiswa.bimbinganCounts ? (
                      <>
                        <div><strong>Total:</strong> {mahasiswa.bimbinganCounts.total} sesi</div>
                        <div><strong>Proposal:</strong> {mahasiswa.bimbinganCounts.proposal} sesi</div>
                        <div><strong>Hasil:</strong> {mahasiswa.bimbinganCounts.hasil} sesi</div>
                        <div><strong>Finalisasi:</strong> {mahasiswa.bimbinganCounts.finalisasi} sesi</div>

                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="mt-2"
                          onClick={() => {
                            if (mahasiswa.tugasAkhir && mahasiswa.tugasAkhir.id) {
                              setSelectedDetailView('bimbingan');
                              setSelectedTugasAkhir(mahasiswa.tugasAkhir);
                            }
                          }}
                          disabled={!mahasiswa.tugasAkhir || !mahasiswa.tugasAkhir.id}
                        >
                          Lihat Detail Bimbingan
                        </Button>

                        <Button
                          variant="outline-success"
                          size="sm"
                          className="mt-1"
                          onClick={() => {
                            if (mahasiswa.tugasAkhir && mahasiswa.tugasAkhir.id) {
                              setSelectedLogbookMahasiswa(mahasiswa);
                              setLogbookMode('both');
                              setShowLogbookModal(true);
                            }
                          }}
                          disabled={!mahasiswa.tugasAkhir || !mahasiswa.tugasAkhir.id}
                        >
                          <FaFileDownload className="me-1" />
                          Generate Logbook
                        </Button>
                      </>
                    ) : '-'}
                  </td>
                  <td>
                    {mahasiswa.allSeminars?.proposal && mahasiswa.allSeminars.proposal.length > 0 ? (
                      <>
                        {mahasiswa.allSeminars.proposal.map((seminar, idx) => (
                          <div key={idx} className={idx > 0 ? "mt-3 pt-3 border-top" : ""}>
                            <Badge bg={
                              seminar.status === 'completed' ? 'success' :
                                seminar.status === 'scheduled' ? 'info' : 'secondary'
                            }>
                              {seminar.status === 'completed' ? 'Selesai' :
                                seminar.status === 'scheduled' ? 'Terjadwal' :
                                  seminar.status}
                            </Badge>

                            {seminar.isReschedule && (
                              <Badge bg="warning" className="ms-1">Ulang</Badge>
                            )}

                            {seminar.status === 'completed' && seminar.nilai && (
                              <div className="mt-1">
                                {Object.keys(seminar.nilai).length === 4 ? (
                                  <>
                                    {(() => {
                                      // Use the standardized score calculation utility
                                      const result = calculateSeminarScore(seminar);
                                      const finalScore = result.finalScore;
                                      const passed = result.isPassed;

                                      return (
                                        <>
                                          <strong>Nilai: {finalScore.toFixed(1)}</strong>{' '}
                                          <Badge bg={passed ? 'success' : 'danger'}>
                                            {passed ? 'LULUS' : 'TIDAK LULUS'}
                                          </Badge>

                                          <div className="mt-1">
                                            <small>Tanggal: {new Date(seminar.tanggal).toLocaleDateString()}</small>
                                          </div>

                                          <Button
                                            variant="outline-primary"
                                            size="sm"
                                            className="mt-1"
                                            onClick={() => {
                                              setSelectedDetailView('seminar');
                                              setSelectedSeminar(seminar);
                                            }}
                                          >
                                            Detail
                                          </Button>
                                        </>
                                      );
                                    })()}
                                  </>
                                ) : (
                                  <>{Object.keys(seminar.nilai).length}/4 penguji</>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </>
                    ) : (
                      <Badge bg="secondary">Belum Terjadwal</Badge>
                    )}
                  </td>
                  <td>
                    {mahasiswa.allSeminars?.hasil && mahasiswa.allSeminars.hasil.length > 0 ? (
                      <>
                        {mahasiswa.allSeminars.hasil.map((seminar, idx) => (
                          <div key={idx} className={idx > 0 ? "mt-3 pt-3 border-top" : ""}>
                            <Badge bg={
                              seminar.status === 'completed' ? 'success' :
                                seminar.status === 'scheduled' ? 'info' : 'secondary'
                            }>
                              {seminar.status === 'completed' ? 'Selesai' :
                                seminar.status === 'scheduled' ? 'Terjadwal' :
                                  seminar.status}
                            </Badge>

                            {seminar.isReschedule && (
                              <Badge bg="warning" className="ms-1">Ulang</Badge>
                            )}

                            {seminar.status === 'completed' && seminar.nilai && (
                              <div className="mt-1">
                                {Object.keys(seminar.nilai).length === 4 ? (
                                  <>
                                    {(() => {
                                      // Use the standardized score calculation utility
                                      const result = calculateSeminarScore(seminar);
                                      const finalScore = result.finalScore;
                                      const passed = result.isPassed;

                                      return (
                                        <>
                                          <strong>Nilai: {finalScore.toFixed(1)}</strong>{' '}
                                          <Badge bg={passed ? 'success' : 'danger'}>
                                            {passed ? 'LULUS' : 'TIDAK LULUS'}
                                          </Badge>

                                          <div className="mt-1">
                                            <small>Tanggal: {new Date(seminar.tanggal).toLocaleDateString()}</small>
                                          </div>

                                          <Button
                                            variant="outline-primary"
                                            size="sm"
                                            className="mt-1"
                                            onClick={() => {
                                              setSelectedDetailView('seminar');
                                              setSelectedSeminar(seminar);
                                            }}
                                          >
                                            Detail
                                          </Button>
                                        </>
                                      );
                                    })()}
                                  </>
                                ) : (
                                  <>{Object.keys(seminar.nilai).length}/4 penguji</>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </>
                    ) : (
                      <Badge bg="secondary">Belum Terjadwal</Badge>
                    )}
                  </td>
                  <td>
                    {mahasiswa.allSeminars?.kompre && mahasiswa.allSeminars.kompre.length > 0 ? (
                      <>
                        {mahasiswa.allSeminars.kompre.map((seminar, idx) => (
                          <div key={idx} className={idx > 0 ? "mt-3 pt-3 border-top" : ""}>
                            <Badge bg={
                              seminar.status === 'completed' ? 'success' :
                                seminar.status === 'scheduled' ? 'info' : 'secondary'
                            }>
                              {seminar.status === 'completed' ? 'Selesai' :
                                seminar.status === 'scheduled' ? 'Terjadwal' :
                                  seminar.status}
                            </Badge>

                            {seminar.isReschedule && (
                              <Badge bg="warning" className="ms-1">Ulang</Badge>
                            )}

                            {seminar.status === 'completed' && seminar.nilai && (
                              <div className="mt-1">
                                {Object.keys(seminar.nilai).length === 4 ? (
                                  <>
                                    {(() => {
                                      // Use the standardized score calculation utility
                                      const result = calculateSeminarScore(seminar);
                                      const finalScore = result.finalScore;
                                      const passed = result.isPassed;

                                      return (
                                        <>
                                          <strong>Nilai: {finalScore.toFixed(1)}</strong>{' '}
                                          <Badge bg={passed ? 'success' : 'danger'}>
                                            {passed ? 'LULUS' : 'TIDAK LULUS'}
                                          </Badge>

                                          <div className="mt-1">
                                            <small>Tanggal: {new Date(seminar.tanggal).toLocaleDateString()}</small>
                                          </div>

                                          <Button
                                            variant="outline-primary"
                                            size="sm"
                                            className="mt-1"
                                            onClick={() => {
                                              setSelectedDetailView('seminar');
                                              setSelectedSeminar(seminar);
                                            }}
                                          >
                                            Detail
                                          </Button>
                                        </>
                                      );
                                    })()}
                                  </>
                                ) : (
                                  <>{Object.keys(seminar.nilai).length}/4 penguji</>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </>
                    ) : (
                      <Badge bg="secondary">Belum Terjadwal</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );
  };

  // Function to render the dosen monitoring page
  const renderDosenMonitoring = () => {
    return (
      <Card>
        <Card.Header className="bg-success text-white">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0"><FaUsers className="me-2" /> Monitoring Dosen</h5>
            <Button variant="light" size="sm" onClick={loadDosenData}>
              Refresh Data
            </Button>
          </div>
        </Card.Header>
        <Card.Body style={{ maxHeight: '600px', overflowY: 'auto' }}>
          <Table responsive striped bordered hover>
            <thead>
              <tr>
                <th>Nama</th>
                <th>NIP</th>
                <th>Email</th>
                <th>Mahasiswa Bimbingan</th>
                <th>Total Bimbingan</th>
                <th>Bimbingan Pending</th>
              </tr>
            </thead>
            <tbody>
              {dosenData.map((dosen, idx) => (
                <tr key={idx}>
                  <td>{dosen.nama}</td>
                  <td>{dosen.nip || '-'}</td>
                  <td>{dosen.email}</td>
                  <td>{dosen.mahasiswaCount || 0}</td>
                  <td>{dosen.bimbinganCounts?.total || 0}</td>
                  <td>
                    {dosen.bimbinganCounts?.pending > 0 ? (
                      <Badge bg="warning" pill>
                        {dosen.bimbinganCounts.pending}
                      </Badge>
                    ) : (
                      <Badge bg="success" pill>0</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );
  };

  // Render the diagnostic tools component
  const renderDiagnosticTools = () => {
    return (
      <Card>
        <Card.Header className="bg-info text-white">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0"><FaTools className="me-2" /> Alat Diagnostik Database</h5>
            <div>
              <Button
                variant="light"
                className="me-2"
                onClick={runDiagnostics}
                disabled={loading}
              >
                <FaDatabase className="me-1" />
                {loading ? 'Menjalankan...' : 'Jalankan Diagnostik'}
              </Button>
              <Button
                variant="warning"
                onClick={fixDatabaseIssues}
                disabled={loading || !diagnosticResults}
              >
                <FaWrench className="me-1" />
                Perbaiki Masalah
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          {diagnosticResults && (
            <div>
              <h5>Hasil Diagnostik:</h5>

              <div className="mb-3">
                <h6>Jumlah Data:</h6>
                <ul>
                  <li>Pengguna: {diagnosticResults.counts.users}</li>
                  <li>Tugas Akhir: {diagnosticResults.counts.tugasAkhir}</li>
                  <li>Bimbingan: {diagnosticResults.counts.bimbingan}</li>
                  <li>Seminar: {diagnosticResults.counts.seminar}</li>
                </ul>
              </div>

              <div className="mb-3">
                <h6>Masalah Terdeteksi:</h6>
                {diagnosticResults.issues.bimbinganWithoutPembimbing === 0 &&
                  diagnosticResults.issues.taWithoutPembimbing === 0 ? (
                  <Alert variant="success">
                    Tidak ada masalah terdeteksi dalam database!
                  </Alert>
                ) : (
                  <Alert variant="warning">
                    <ul className="mb-0">
                      {diagnosticResults.issues.bimbinganWithoutPembimbing > 0 && (
                        <li>Terdapat {diagnosticResults.issues.bimbinganWithoutPembimbing} data bimbingan tanpa pembimbingId</li>
                      )}
                      {diagnosticResults.issues.taWithoutPembimbing > 0 && (
                        <li>Terdapat {diagnosticResults.issues.taWithoutPembimbing} tugas akhir yang disetujui tanpa pembimbingId</li>
                      )}
                      {diagnosticResults.issues.pendingBimbingan > 0 && (
                        <li>Terdapat {diagnosticResults.issues.pendingBimbingan} bimbingan dengan status pending</li>
                      )}
                    </ul>
                  </Alert>
                )}
              </div>
            </div>
          )}

          <Button
            variant="success"
            onClick={async () => {
              try {
                setLoading(true);
                setError('');
                setSuccess('');

                // Get all approved tugas akhir
                const taQuery = query(
                  collection(db, "tugasAkhir"),
                  where("status", "==", "approved")
                );
                const taSnapshot = await getDocs(taQuery);

                let createdCount = 0;

                for (const taDoc of taSnapshot.docs) {
                  const taData = taDoc.data();
                  const taId = taDoc.id;

                  // Skip if no pembimbingId
                  if (!taData.pembimbingId) continue;

                  // Convert to array if string
                  const pembimbingArray = Array.isArray(taData.pembimbingId)
                    ? taData.pembimbingId
                    : [taData.pembimbingId];

                  // For each pembimbing
                  for (const pembimbingId of pembimbingArray) {
                    // Check if bimbingan already exists
                    const bimbinganQuery = query(
                      collection(db, "bimbingan"),
                      where("tugasAkhirId", "==", taId),
                      where("pembimbingId", "==", pembimbingId)
                    );
                    const bimbinganSnapshot = await getDocs(bimbinganQuery);

                    // If no bimbingan exists, create one
                    if (bimbinganSnapshot.empty) {
                      // Get pembimbing name
                      let dosenNama = "Dosen";
                      const dosenDoc = await getDoc(doc(db, "users", pembimbingId));
                      if (dosenDoc.exists()) {
                        dosenNama = dosenDoc.data().nama || "Dosen";
                      }

                      // Get mahasiswa name
                      let mahasiswaNama = "Mahasiswa";
                      if (taData.mahasiswaId) {
                        const mahasiswaDoc = await getDoc(doc(db, "users", taData.mahasiswaId));
                        if (mahasiswaDoc.exists()) {
                          mahasiswaNama = mahasiswaDoc.data().nama || "Mahasiswa";
                        }
                      }

                      // Create bimbingan
                      const bimbinganData = {
                        tugasAkhirId: taId,
                        mahasiswaId: taData.mahasiswaId,
                        pembimbingId: pembimbingId,
                        sesi: 0,
                        judul: `Pengantar Bimbingan - ${taData.judul || "Tugas Akhir"}`,
                        fileURL: taData.driveLink || "https://drive.google.com/",
                        fileName: '',
                        catatan: `Bimbingan awal untuk tugas akhir. Silakan mahasiswa membuat jadwal konsultasi dengan dosen pembimbing.`,
                        status: 'reviewed',
                        tanggal: new Date().toISOString(),
                        tanggalRevisi: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
                        reviewCatatan: `Selamat ${mahasiswaNama}, tugas akhir Anda telah disetujui. ${dosenNama} telah ditugaskan sebagai pembimbing Anda.`,
                        reviewDriveLink: '',
                        tanggalReview: new Date().toISOString(),
                        createdAt: new Date().toISOString(),
                        isAdminCreated: true
                      };

                      await addDoc(collection(db, "bimbingan"), bimbinganData);
                      createdCount++;
                    }
                  }
                }

                setSuccess(`Bimbingan pengantar selesai dibuat. ${createdCount} bimbingan baru dibuat.`);

                // Refresh data sesuai tab aktif
                if (activeTab === 'dashboard') {
                  loadDashboardData();
                } else if (activeTab === 'mahasiswa') {
                  loadMahasiswaData();
                } else if (activeTab === 'dosen') {
                  loadDosenData();
                }
                // Run diagnostics again
                runDiagnostics();

              } catch (error) {
                console.error("Error creating bimbingan entries:", error);
                setError("Terjadi kesalahan: " + error.message);
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading}
            className="mt-3"
          >
            <FaBook className="me-1" />
            Buat Bimbingan Pengantar
          </Button>
        </Card.Body>
      </Card>
    );
  };

  // Render koordinator Tugas Akhir settings
  const renderKoordinatorSettings = () => {
    // Show loading if data is being fetched
    if (loading && dosenData.length === 0) {
      return (
        <Card>
          <Card.Header className="bg-primary text-white">
            <h5 className="mb-0"><FaUserTie className="me-2" /> Pengaturan Koordinator Tugas Akhir</h5>
          </Card.Header>
          <Card.Body>
            <Alert variant="info">Memuat data dosen...</Alert>
          </Card.Body>
        </Card>
      );
    }

    return (
      <Card>
        <Card.Header className="bg-primary text-white">
          <h5 className="mb-0"><FaUserTie className="me-2" /> Pengaturan Koordinator Tugas Akhir</h5>
        </Card.Header>
        <Card.Body>
          <Alert variant="info">
            Koordinator Tugas Akhir akan muncul sebagai penandatangan pada dokumen undangan seminar yang di-generate.
          </Alert>

          <Row className="mb-4">
            <Col md={6}>
              <h6>Koordinator Saat Ini:</h6>
              {koordinatorTA ? (
                <div className="border rounded p-3">
                  <p className="mb-1"><strong>Nama:</strong> {koordinatorTA.nama || '-'}</p>
                  <p className="mb-1"><strong>NIP:</strong> {koordinatorTA.nip || '-'}</p>
                  <p className="mb-0"><strong>Email:</strong> {koordinatorTA.email || '-'}</p>
                </div>
              ) : (
                <Alert variant="warning">
                  Belum ada koordinator tugas akhir yang ditetapkan.
                </Alert>
              )}
            </Col>
            <Col md={6}>
              <h6>Pilih Koordinator Baru:</h6>
              {loading && dosenData.length === 0 ? (
                <Alert variant="info">Memuat data dosen...</Alert>
              ) : dosenData.length === 0 ? (
                <div>
                  <Alert variant="warning" className="mb-3">
                    Tidak ada data dosen. Pastikan ada dosen yang sudah terverifikasi.
                  </Alert>
                  <Button
                    variant="outline-primary"
                    onClick={async () => {
                      // Load dosen data untuk settings
                      try {
                        setLoading(true);
                        const dosenQuery = query(
                          collection(db, "users"),
                          where("role", "==", "dosen"),
                          where("verified", "==", true)
                        );
                        const dosenSnapshot = await getDocs(dosenQuery);
                        const dosenList = dosenSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                        setDosenData(dosenList);
                        setSuccess("Data dosen berhasil dimuat");
                      } catch (error) {
                        console.error("Error loading dosen data:", error);
                        setError("Terjadi kesalahan saat memuat data dosen");
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading}
                  >
                    {loading ? 'Memuat...' : 'Refresh Data Dosen'}
                  </Button>
                </div>
              ) : (
                <>
                  <Form.Select
                    value={selectedKoordinatorId}
                    onChange={(e) => setSelectedKoordinatorId(e.target.value)}
                    className="mb-3"
                  >
                    <option value="">-- Pilih Dosen --</option>
                    {dosenData.map((dosen) => (
                      <option key={dosen.id} value={dosen.id}>
                        {dosen.nama || 'Dosen'} {dosen.nip ? `(${dosen.nip})` : ''}
                      </option>
                    ))}
                  </Form.Select>
                  <Button
                    variant="primary"
                    onClick={handleSetKoordinatorTA}
                    disabled={loading || !selectedKoordinatorId}
                  >
                    {loading ? 'Menyimpan...' : 'Set Koordinator'}
                  </Button>
                </>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>
    );
  };

  return (
    <Container className="py-4">
      <h1>Admin Panel</h1>

      {/* Messages */}
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}
      {loading && <Alert variant="info">Memuat data...</Alert>}

      {/* Navigation menu */}
      <Nav variant="tabs" className="mb-3" activeKey={activeTab} onSelect={k => {
        setActiveTab(k);
        if (!loadedTabs.has(k)) {
          if (k === 'dashboard') {
            loadDashboardData();
          } else if (k === 'mahasiswa') {
            loadMahasiswaData();
          } else if (k === 'dosen') {
            loadDosenData();
          }
        }
      }}>
        <Nav.Item>
          <Nav.Link eventKey="dashboard">
            <FaChartBar className="me-1" /> Dashboard
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="mahasiswa">
            <FaUsers className="me-1" /> Monitoring Mahasiswa
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="dosen">
            <FaUsers className="me-1" /> Monitoring Dosen
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="tools">
            <FaTools className="me-1" /> Tools
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="settings">
            <FaCog className="me-1" /> Pengaturan
          </Nav.Link>
        </Nav.Item>
      </Nav>

      {/* Main content based on tab */}
      {activeTab === 'dashboard' && renderDashboard()}
      {activeTab === 'mahasiswa' && renderMahasiswaMonitoring()}
      {activeTab === 'dosen' && renderDosenMonitoring()}
      {activeTab === 'tools' && renderDiagnosticTools()}
      {activeTab === 'settings' && renderKoordinatorSettings()}

      {/* Bimbingan Detail Modal */}
      <Modal
        show={selectedDetailView === 'bimbingan'}
        onHide={() => setSelectedDetailView(null)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Detail Bimbingan</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTugasAkhir && (
            <div>
              <h5>{selectedTugasAkhir.judul}</h5>
              <p><strong>ID Tugas Akhir:</strong> {selectedTugasAkhir.id}</p>

              {loading ? (
                <Alert variant="info">Memuat data bimbingan...</Alert>
              ) : (
                <BimbinganDetailView tugasAkhirId={selectedTugasAkhir.id} />
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSelectedDetailView(null)}>
            Tutup
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Seminar Detail Modal */}
      <Modal
        show={selectedDetailView === 'seminar'}
        onHide={() => setSelectedDetailView(null)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Detail {selectedSeminar?.jenis === 'proposal' ? 'Seminar Proposal' :
              selectedSeminar?.jenis === 'hasil' ? 'Seminar Hasil' : 'Sidang Komprehensif'}
            {selectedSeminar?.isReschedule && (
              <Badge bg="warning" className="ms-2">Ulang</Badge>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSeminar && (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>Tanggal:</strong> {new Date(selectedSeminar.tanggal).toLocaleDateString()}</p>
                  <p><strong>Waktu:</strong> {selectedSeminar.waktu || '-'}</p>
                  <p><strong>Tempat:</strong> {selectedSeminar.tempat || '-'}</p>
                  <p>
                    <strong>Status:</strong>{' '}
                    <Badge bg={
                      selectedSeminar.status === 'scheduled' ? 'info' :
                        selectedSeminar.status === 'completed' ? 'success' :
                          selectedSeminar.status === 'cancelled' ? 'danger' : 'secondary'
                    }>
                      {selectedSeminar.status === 'scheduled' ? 'Terjadwal' :
                        selectedSeminar.status === 'completed' ? 'Selesai' :
                          selectedSeminar.status === 'cancelled' ? 'Dibatalkan' : selectedSeminar.status}
                    </Badge>
                  </p>
                </Col>
                <Col md={6}>
                  <p><strong>Mahasiswa:</strong> {selectedSeminar.mahasiswaNama || selectedSeminar.mahasiswa?.nama || '-'}</p>
                  <p><strong>NIM:</strong> {selectedSeminar.mahasiswaNIM || selectedSeminar.mahasiswa?.nim || '-'}</p>
                  <p><strong>Judul TA:</strong> {selectedSeminar.tugasAkhir?.judul || selectedSeminar.judulTA || '-'}</p>
                </Col>
              </Row>

              <hr />

              <h6>Dosen Penguji:</h6>
              <ul>
                {selectedSeminar.pengujiNama?.map((nama, index) => (
                  <li key={index}>
                    {nama}
                    {index === 0 && selectedSeminar.tugasAkhir?.pembimbingId?.[0] === selectedSeminar.pengujiId?.[index] &&
                      <Badge bg="primary" className="ms-2">Pembimbing 1</Badge>
                    }
                    {index === 1 && selectedSeminar.tugasAkhir?.pembimbingId?.[1] === selectedSeminar.pengujiId?.[index] &&
                      <Badge bg="secondary" className="ms-2">Pembimbing 2</Badge>
                    }
                    {index === 2 && <Badge bg="info" className="ms-2">Penguji 1</Badge>}
                    {index === 3 && <Badge bg="dark" className="ms-2">Penguji 2</Badge>}
                  </li>
                ))}
              </ul>

              {selectedSeminar.kriteriaPenilaian && selectedSeminar.kriteriaPenilaian.length > 0 && (
                <div className="mt-4">
                  <h6>Kriteria Penilaian:</h6>
                  <Table responsive striped bordered>
                    <thead>
                      <tr>
                        <th>Kriteria</th>
                        <th>Bobot</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedSeminar.kriteriaPenilaian.map((kriteria, index) => (
                        <tr key={index}>
                          <td>{kriteria.nama}</td>
                          <td>{kriteria.bobot}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

              {selectedSeminar.nilai && Object.keys(selectedSeminar.nilai).length > 0 && (
                <div className="mt-4">
                  <h6>Hasil Penilaian:</h6>
                  <Table responsive striped bordered>
                    <thead>
                      <tr>
                        <th>Penguji</th>
                        <th>Nilai</th>
                        <th>Bobot</th>
                        <th>Kontribusi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.keys(selectedSeminar.nilai).map((pengujiId, index) => {
                        const pengujiNilai = selectedSeminar.nilai[pengujiId];
                        const pengujiIndex = typeof selectedSeminar.pengujiId !== 'undefined' &&
                          Array.isArray(selectedSeminar.pengujiId) ?
                          selectedSeminar.pengujiId.indexOf(pengujiId) : -1;
                        const pengujiNama = pengujiIndex >= 0 && Array.isArray(selectedSeminar.pengujiNama) ?
                          selectedSeminar.pengujiNama[pengujiIndex] :
                          `Penguji ${index + 1}`;

                        // Calculate score for this penguji
                        let total = 0;
                        let count = 0;

                        if (Array.isArray(selectedSeminar.kriteriaPenilaian)) {
                          selectedSeminar.kriteriaPenilaian.forEach(kriteria => {
                            if (pengujiNilai && kriteria && kriteria.id && pengujiNilai[kriteria.id]) {
                              total += pengujiNilai[kriteria.id] * (kriteria.bobot / 100);
                              count++;
                            }
                          });
                        }

                        // Determine role for display
                        const isPembimbing1 = selectedSeminar.tugasAkhir &&
                          typeof selectedSeminar.tugasAkhir === 'object' &&
                          Array.isArray(selectedSeminar.tugasAkhir.pembimbingId) &&
                          selectedSeminar.tugasAkhir.pembimbingId.length > 0 &&
                          pengujiId === selectedSeminar.tugasAkhir.pembimbingId[0];

                        const isPembimbing2 = selectedSeminar.tugasAkhir &&
                          typeof selectedSeminar.tugasAkhir === 'object' &&
                          Array.isArray(selectedSeminar.tugasAkhir.pembimbingId) &&
                          selectedSeminar.tugasAkhir.pembimbingId.length > 1 &&
                          pengujiId === selectedSeminar.tugasAkhir.pembimbingId[1];

                        // For display in the table, we'll show the role and correct weight
                        let weight = isPembimbing1 ? 0.35 :
                          isPembimbing2 ? 0.25 :
                            index === 0 || index === 1 ? 0.20 : 0.20;

                        const average = count > 0 ? total : 0;
                        const contribution = average * weight;

                        // Add role label to the name for clarity
                        const roleLabel = isPembimbing1 ? ' (Pembimbing 1)' :
                          isPembimbing2 ? ' (Pembimbing 2)' :
                            weight === 0.20 && index === 2 ? ' (Penguji 1)' : ' (Penguji 2)';

                        return (
                          <tr key={index}>
                            <td>{pengujiNama}{roleLabel}</td>
                            <td>{average.toFixed(2)}</td>
                            <td>{(weight * 100).toFixed(0)}%</td>
                            <td>{contribution.toFixed(2)}</td>
                          </tr>
                        );
                      })}

                      <tr className="table-primary">
                        <td colSpan="3"><strong>Nilai Akhir</strong></td>
                        <td>
                          <strong>
                            {calculateFinalScore(selectedSeminar).toFixed(2)}
                          </strong>
                        </td>
                      </tr>
                    </tbody>
                  </Table>

                  <Alert variant="info">
                    <strong>Keterangan Pembobotan:</strong>
                    <ul className="mb-0">
                      <li>Pembimbing 1: 35%</li>
                      <li>Pembimbing 2: 25%</li>
                      <li>Penguji 1: 20%</li>
                      <li>Penguji 2: 20%</li>
                    </ul>
                  </Alert>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSelectedDetailView(null)}>
            Tutup
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Logbook Generation Modal */}
      <Modal show={showLogbookModal} onHide={() => setShowLogbookModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Generate Logbook Bimbingan</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedLogbookMahasiswa && (
            <>
              <p><strong>Mahasiswa:</strong> {selectedLogbookMahasiswa.nama}</p>
              <p><strong>NIM:</strong> {selectedLogbookMahasiswa.nim}</p>
              <p><strong>Judul TA:</strong> {selectedLogbookMahasiswa.tugasAkhir?.judul}</p>

              <Form.Group className="mt-3">
                <Form.Label><strong>Pilih Pembimbing:</strong></Form.Label>
                <Form.Select
                  value={logbookMode}
                  onChange={(e) => setLogbookMode(e.target.value)}
                >
                  <option value="both">Gabung Semua Pembimbing</option>
                  <option value="p1">Pembimbing 1 Saja</option>
                  <option value="p2">Pembimbing 2 Saja</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  {logbookMode === 'both'
                    ? 'Semua bimbingan dari P1 & P2 akan digabung dalam 1 PDF dengan keterangan pembimbing'
                    : logbookMode === 'p1'
                      ? 'Hanya bimbingan dari Pembimbing 1 yang akan ditampilkan'
                      : 'Hanya bimbingan dari Pembimbing 2 yang akan ditampilkan'}
                </Form.Text>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLogbookModal(false)}>
            Batal
          </Button>
          <Button
            variant="success"
            onClick={handleGenerateLogbook}
            disabled={loadingLogbook}
          >
            <FaFileDownload className="me-1" />
            {loadingLogbook ? 'Generating...' : 'Generate PDF'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Admin;